import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type ScrollLockSnapshot = {
  bodyOverflow: string;
  bodyPosition: string;
  bodyTop: string;
  bodyLeft: string;
  bodyRight: string;
  bodyWidth: string;
  bodyPaddingRight: string;
  scrollY: number;
};

type UseModalFocusOptions = {
  /**
   * Lock body scroll in useLayoutEffect before paint (plan fees drawer).
   * Default false keeps master timing so confirm details sheet does not get a
   * false inner scrollbar from vh shifting under position:fixed.
   */
  lockBeforePaint?: boolean;
};

let bodyScrollLockCount = 0;
let scrollLockSnapshot: ScrollLockSnapshot | undefined;

/**
 * Ref-counted body scroll lock.
 *
 * Uses `position: fixed` (not `html { overflow: hidden }`) so iOS Safari can
 * restore vertical scrolling after overlays that involve the software keyboard
 * — locking `documentElement.overflow` is a known way to leave the page
 * permanently unscrollable on mobile.
 */
function lockBodyScroll() {
  if (bodyScrollLockCount === 0) {
    const { body } = document;
    const scrollY = window.scrollY;
    const scrollbarWidth = Math.max(
      0,
      window.innerWidth - document.documentElement.clientWidth,
    );

    scrollLockSnapshot = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyPaddingRight: body.style.paddingRight,
      scrollY,
    };

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }
  bodyScrollLockCount += 1;
}

function unlockBodyScroll() {
  if (bodyScrollLockCount <= 0) return;

  bodyScrollLockCount -= 1;
  if (bodyScrollLockCount !== 0) return;

  const snapshot = scrollLockSnapshot;
  scrollLockSnapshot = undefined;
  if (!snapshot) return;

  const { body } = document;
  body.style.overflow = snapshot.bodyOverflow;
  body.style.position = snapshot.bodyPosition;
  body.style.top = snapshot.bodyTop;
  body.style.left = snapshot.bodyLeft;
  body.style.right = snapshot.bodyRight;
  body.style.width = snapshot.bodyWidth;
  body.style.paddingRight = snapshot.bodyPaddingRight;
  window.scrollTo(0, snapshot.scrollY);
}

/**
 * Lock body scroll, focus initial control on open, trap Tab within the dialog,
 * restore scroll and focus on close.
 */
export function useModalFocus(
  isOpen: boolean,
  containerRef: RefObject<HTMLElement | null>,
  initialFocusRef?: RefObject<HTMLElement | null>,
  options?: UseModalFocusOptions,
) {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const lockBeforePaint = options?.lockBeforePaint ?? false;

  useLayoutEffect(() => {
    if (!lockBeforePaint || !isOpen) return;

    lockBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [isOpen, lockBeforePaint]);

  useEffect(() => {
    if (!isOpen) return;

    if (!lockBeforePaint) {
      lockBodyScroll();
    }

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const focusInitial = () => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus({ preventScroll: true });
        return;
      }
      const container = containerRef.current;
      if (!container) return;
      const first = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus({ preventScroll: true });
    };
    requestAnimationFrame(focusInitial);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !containerRef.current) return;

      const focusables = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.tabIndex !== -1);

      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      try {
        // preventScroll avoids fighting scroll restoration after position:fixed unlock.
        previousFocusRef.current?.focus?.({ preventScroll: true });
      } finally {
        if (!lockBeforePaint) {
          unlockBodyScroll();
        }
      }
    };
  }, [isOpen, containerRef, initialFocusRef, lockBeforePaint]);
}
